import { lazy, type ComponentType } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { REDIRECTS } from './pages'
import type { CatalogState } from './state/catalogState'
import type { ProjectsState } from './state/projects'
import type { ReportsState } from './state/reports'
import { EntryRoute } from './state/route'
import type { ThemeChoice } from './state/theme'
import type { ContrastChoice } from './state/contrast'
import { Welcome } from './views/Welcome'

/*
 * Every page but the first one a visitor sees is loaded when it is opened, so
 * the start page does not wait for the code of the others.
 */
function named<M extends Record<K, ComponentType<any>>, K extends string>(
  load: () => Promise<M>,
  name: K,
) {
  return lazy(() => load().then((module) => ({ default: module[name] })))
}

const AssessmentPage = named(() => import('./views/AssessmentPage'), 'AssessmentPage')
const Settings = named(() => import('./views/Settings'), 'Settings')

/**
 * Every address the application answers to in the minimal version:
 * Welcome, Assessment, and Settings.
 */
export function AppRoutes({
  state,
  catalog,
  theme,
  onTheme,
  contrast,
  onContrast,
  accent,
  onAccent,
  palette,
  onPalette,
  guide,
}: {
  state: ReportsState
  catalog: CatalogState
  projects?: ProjectsState
  theme: ThemeChoice
  onTheme: (theme: ThemeChoice) => void
  contrast: ContrastChoice
  onContrast: (contrast: ContrastChoice) => void
  accent: string
  onAccent: (id: string) => void
  palette: string
  onPalette: (id: string) => void
  softwareCount?: number
  guide?: { remaining: number; total: number; open: () => void }
}) {
  const hasData = state.reports.length > 0
  return (
    <Routes>
      <Route
        path="/welcome"
        element={
          hasData ? <FromWelcome /> : <Welcome state={state} catalog={catalog} guide={guide} />
        }
      />

      <Route
        path="/settings"
        element={
          <Settings
            theme={theme}
            onTheme={onTheme}
            contrast={contrast}
            onContrast={onContrast}
            accent={accent}
            onAccent={onAccent}
            palette={palette}
            onPalette={onPalette}
            catalog={catalog}
            reportCount={state.reports.length}
            onResetData={state.clear}
          />
        }
      />

      {/* Every address a page used to have, so old links keep working. */}
      {Object.entries(REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<Redirect to={to} />} />
      ))}

      <Route path="/" element={<EntryRoute waiting={state.restoring} resume={hasData} />} />

      {hasData ? (
        <>
          <Route
            path="/assessment"
            element={
              <AssessmentPage
                reports={state.reports}
                registry={state.registry}
                onRemoveReport={state.remove}
                onResetData={state.clear}
              />
            }
          />
          <Route path="*" element={<Navigate to="/assessment" replace />} />
        </>
      ) : (
        <>
          <Route path="/assessment" element={<ToWelcome />} />
          <Route path="*" element={<ToWelcome />} />
        </>
      )}
    </Routes>
  )
}

/**
 * A redirect that keeps the query, so a shared link to an old address still
 * opens what it named. The query of `to`, such as a section, is added to it.
 */
function Redirect({ to }: { to: string }) {
  const { search } = useLocation()
  const [pathname, extra] = to.split('?')
  const params = new URLSearchParams(search)
  if (extra) {
    new URLSearchParams(extra).forEach((value, key) => params.set(key, value))
  }
  return <Navigate to={{ pathname, search: `${params}` }} replace />
}

/** Past the Welcome page: to the page that was asked for, or the assessment, query kept. */
function FromWelcome() {
  const { search, state } = useLocation()
  const from = (state as { from?: string } | null)?.from
  return <Navigate to={from ?? `/assessment${search}`} replace />
}

/** To the Welcome page, remembering the page that was asked for. */
function ToWelcome() {
  const { pathname, search } = useLocation()
  return <Navigate to="/welcome" replace state={{ from: `${pathname}${search}` }} />
}
